import { Box, Typography, Divider } from "@mui/material";
import TextInput from "../../../../components/textinput/textinput";
import { useState } from "react";
import "./add-client.css";
import DialogContainer from "../../../../components/dialog-container/dialog-container";
import { useFormik } from "formik";
import * as yup from "yup";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import { addClientRequest } from "../../../../utils/APICalls";
import Loading from "../../../../components/loading/loading";
import { emailRegex } from "../../../../utils/Constant";

type props = {
  showDialog: boolean;
  closeDialog: any;
  addUser: any;
};

type FormValues = {
  clientName: string;
  website: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

const ValidationSchema = yup.object({
  clientName: yup.string().required("This field can't be empty"),
  // website: yup.string().matches(websiteRegex, "Invalid Website"),
  firstName: yup.string().required("This field can't be empty"),
  lastName: yup.string().required("This field can't be empty"),
  email: yup
    .string()
    .required("This field can't be empty")
    .matches(emailRegex, "Invalid Email Address"),
  phone: yup
    .string()
    .required("This field can't be empty")
    .max(50, "Maximum 50 characters allowed"),
});

export default function AddClientDialog({
  showDialog,
  closeDialog,
  addUser,
}: props) {
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const formik = useFormik<FormValues>({
    initialValues: {
      clientName: "",
      website: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      addClient(values);
    },
  });

  const addClient = async (data: any) => {
    let finalWebsite = null;
    if (data.website.length > 0) {
      finalWebsite = data.website.includes("http")
        ? data.website
        : `https://${data.website}`;
    }
    const payload = {
      clientName: data.clientName,
      website: finalWebsite,
      primaryContact: {
        emailAddress: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phone,
      },
    };
    setShowLoading(true);
    const response = await addClientRequest(payload);
    setShowLoading(false);
    if (response?.status === 201) {
      addUser(response?.data);
      hideDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const hideDialog = () => {
    formik.resetForm();
    closeDialog();
  };

  return (
    <DialogContainer
      title="Add Client"
      showDialog={showDialog}
      closeDialog={hideDialog}
      buttonText="Add Client"
      onSubmit={formik.handleSubmit}
    >
      <form onSubmit={formik.handleSubmit}>
        <Typography variant="h5">Client Details</Typography>
        <Box className="field-layout">
          <Typography className="field-label-txt">Client Name</Typography>
          <TextInput
            className="field-input"
            id="clientName"
            name="clientName"
            label="Client Name"
            variant="outlined"
            value={formik.values.clientName}
            onChange={formik.handleChange}
            error={
              formik.touched.clientName && Boolean(formik.errors.clientName)
            }
            helperText={formik.touched.clientName && formik.errors.clientName}
          />
        </Box>
        <Divider />
        <Box className="field-layout">
          <Typography className="field-label-txt">Website</Typography>
          <TextInput
            className="field-input"
            id="website"
            name="website"
            label="Website"
            variant="outlined"
            value={formik.values.website}
            onChange={formik.handleChange}
            error={formik.touched.website && Boolean(formik.errors.website)}
            helperText={formik.touched.website && formik.errors.website}
          />
        </Box>

        <Typography variant="h5" sx={{ mt: 2 }}>
          Primary Account Manager/Admin
        </Typography>
        <Box className="field-layout">
          <Typography className="field-label-txt">Name</Typography>
          <Box sx={{ display: "flex", width: "calc(100% - 180px)" }}>
            <TextInput
              sx={{ flex: 1, mr: 2 }}
              id="firstName"
              name="firstName"
              label="First Name"
              variant="outlined"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              error={
                formik.touched.firstName && Boolean(formik.errors.firstName)
              }
              helperText={formik.touched.firstName && formik.errors.firstName}
            />
            <TextInput
              sx={{ flex: 1 }}
              id="lastName"
              name="lastName"
              label="Last Name"
              variant="outlined"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
            />
          </Box>
        </Box>
        <Divider />
        <Box className="field-layout">
          <Typography className="field-label-txt">Email</Typography>
          <TextInput
            className="field-input"
            id="email"
            name="email"
            label="Email"
            variant="outlined"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </Box>
        <Divider />
        <Box className="field-layout">
          <Typography className="field-label-txt">Phone</Typography>
          <TextInput
            className="field-input"
            id="phone"
            name="phone"
            label="Phone"
            variant="outlined"
            value={formik.values.phone}
            onChange={formik.handleChange}
            slotProps={{ htmlInput: { maxLength: 50 } }}
            error={formik.touched.phone && Boolean(formik.errors.phone)}
            helperText={formik.touched.phone && formik.errors.phone}
          />
        </Box>
      </form>
      {showLoading && <Loading />}
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
    </DialogContainer>
  );
}
